import BylineList from 'components/Byline';
import Link from 'components/Globals/Base/Link';
import { ARTICLE_PAGE_TEST_IDS } from 'constants/testsIds/articlePage';
import useDateTime from 'hooks/useDateTime';
import type { Author } from 'interfaces/content/Authors';
import type { MediaItem } from 'interfaces/content/Media';
import type { GridAuthorWithUri } from 'interfaces/content/Ranking';
import BLURRED_GRAY_PLACEHOLDER from 'lib/mockData/Image/placeholderUrl';
import dynamic from 'next/dynamic';
import React, { type ForwardedRef } from 'react';
import type { EventData } from 'services/Gtm/entities';
import type { WebTarget } from 'styled-components';
import RawHtml from 'utils/miscUtils/rawHtml';
import { parseCaptionWhenAltTextEmpty } from 'utils/parsingUtils/clientSideParsing';
import * as CS from './CommonStyle';
import * as S from './PostStyles';

export interface PostProps {
  as?: WebTarget;
  image: MediaItem | string | null;
  externalUrl?: string;
  eyebrowSectionLabel: string;
  eyebrowSectionLink: string;
  eyebrowTopicLabel?: string;
  eyebrowTopicLink?: string;
  title: string;
  titleLink: string;
  type?: string;
  authors: Author[] | GridAuthorWithUri[];
  date: string;
  trackingData?: EventData;
  priority?: boolean;
  className?: string;
  isHiddenSlide?: boolean;
}

const NextCustomImage = dynamic(() => import('components/Image'));

const Post = React.forwardRef((props: PostProps, ref: ForwardedRef<HTMLLIElement> | null) => {
  const {
    image,
    title,
    titleLink: rawTitleLink,
    externalUrl,
    type,
    eyebrowSectionLabel,
    eyebrowSectionLink,
    eyebrowTopicLabel,
    eyebrowTopicLink,
    authors,
    date,
    trackingData,
    priority = false,
    className,
    isHiddenSlide,
    as = 'li',
  } = props;
  const localDate = useDateTime(date, 'date');
  const titleLink = externalUrl || rawTitleLink;
  const recommendsArticle = titleLink.match(/recommends/g);
  const normalizedEyebrowTopicLink = eyebrowTopicLink?.endsWith('/') ? eyebrowTopicLink : `${eyebrowTopicLink}/`;
  const normalizedEyebrowSectionLink = eyebrowSectionLink?.endsWith('/')
    ? eyebrowSectionLink
    : `${eyebrowSectionLink}/`;
  // this comes from the article page for latest component that renders both the primary section and primary topic
  const showEyebrowTopic =
    eyebrowTopicLabel && eyebrowTopicLink ? (
      <>
        <CS.Hyphen
          className='hyphen'
          aria-hidden='true'
        >
          {' '}
          -{' '}
        </CS.Hyphen>
        <CS.SectionLink
          $type={type}
          className='link-wrapper'
        >
          <Link
            className='topic-link'
            dataCy='postEyebrow'
            href={normalizedEyebrowTopicLink}
            trackingData={
              trackingData && {
                ...trackingData,
                eventLabel: `category click : ${trackingData.eventLabel || eyebrowTopicLabel}`,
              }
            }
            tabIndex={isHiddenSlide ? -1 : 0}
            aria-hidden={isHiddenSlide ? 'true' : undefined}
            ariaLabel={`Go to article's topic ${eyebrowTopicLabel}`}
            type='red'
          >
            {eyebrowTopicLabel}
          </Link>
        </CS.SectionLink>
      </>
    ) : null;

  const renderEyebrowSectionLink = () => (
    <CS.SectionLink
      $type={type}
      className='link-wrapper'
    >
      <Link
        className='section-link'
        dataCy='postEyebrow'
        href={normalizedEyebrowSectionLink}
        type='red'
        trackingData={
          trackingData && {
            ...trackingData,
            eventLabel: `category click : ${trackingData.eventLabel || eyebrowSectionLabel}`,
          }
        }
        tabIndex={isHiddenSlide ? -1 : 0}
        aria-hidden={isHiddenSlide ? 'true' : undefined}
        ariaLabel={`Go to article's section ${eyebrowSectionLabel}`}
      >
        {eyebrowSectionLabel}
      </Link>
    </CS.SectionLink>
  );

  return (
    <S.Wrapper
      ref={ref}
      className={className}
      as={as}
    >
      <S.Card
        className='card'
        data-cy={ARTICLE_PAGE_TEST_IDS.WRAPPER}
      >
        <NextCustomImage
          className='card-image'
          src={typeof image === 'string' ? image : image?.mediaItemUrl || BLURRED_GRAY_PLACEHOLDER}
          alt={parseCaptionWhenAltTextEmpty(image || 'Fortune Image')}
          width={735}
          height={490}
          sizes='(max-width: 500px) 60vw, (max-width: 768px) 100vw, 10vw'
          priority={priority}
          data-cy={ARTICLE_PAGE_TEST_IDS.IMAGE}
        />
        {eyebrowSectionLabel !== 'No Primary Section Found' &&
          eyebrowSectionLabel &&
          (type !== 'article-redesign-latest' ? (
            renderEyebrowSectionLink()
          ) : (
            <div className='eyebrow'>
              {renderEyebrowSectionLink()}
              {showEyebrowTopic}
            </div>
          ))}
        <S.TitleLink
          className='title-link-wrapper'
          $hasEyebrow={eyebrowSectionLabel !== undefined && eyebrowSectionLabel !== 'No Primary Section Found'}
        >
          <Link
            dataCy='postTitle'
            href={titleLink}
            trackingData={
              trackingData && { ...trackingData, eventLabel: `title click : ${trackingData.eventLabel || title}` }
            }
            className='title-link'
            type='primary'
            tabIndex={isHiddenSlide ? -1 : 0}
            aria-hidden={isHiddenSlide ? 'true' : undefined}
            ariaLabel={`Go to full article: ${title}`}
          >
            {RawHtml({ html: title })}
          </Link>
        </S.TitleLink>
        <CS.CardInfoWrapper
          data-cy='postAuthors'
          className='card-info-wrapper'
        >
          <BylineList
            className='author-list'
            bylineList={authors}
            trackingData={trackingData}
            isHiddenSlide={isHiddenSlide}
          />
          {!recommendsArticle && (
            <S.PubDate
              data-cy='postDate'
              className='date'
            >
              {localDate}
            </S.PubDate>
          )}
        </CS.CardInfoWrapper>
      </S.Card>
    </S.Wrapper>
  );
});

export default Post;
